// BeneficiaryDetail — single-bene drill-down: the payee's bank info
// on top, then every payout dispatched to them (newest first).
//
// Route: /beneficiaries/:beneficiaryId (wired in router.tsx under
// RequireAuth → App). Operators land here by clicking a row on
// /beneficiaries. Any merchant member (viewer+) can read; admin
// short-circuits both server-side gates so the same render works for
// platform-admin views via the merchant switcher.
//
// Money handling (CLAUDE.md rule 6): payout amounts are bigint INR
// paise; rendered via fmtCcyMinor and never coerced through Number().
import { useState, type FormEvent, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, Pill, StatusPill } from "@/components/UI";
import { fmtCcyMinor, formatDateTime } from "@/utils/format";
import { apiErrorMessage, extractVerifyFailedDetail } from "@/utils/apiError";
import { useBeneficiary } from "@/queries/useBeneficiary";
import { useBankAccounts } from "@/queries/useBankAccounts";
import { verifyBeneficiary } from "@/api/beneficiaries";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";

export function BeneficiaryDetail() {
  const navigate = useNavigate();
  const { beneficiaryId: bidParam } = useParams<{ beneficiaryId: string }>();
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));

  // Per-merchant membership role — penny-drop requires operator+ at the
  // backend, so the panel mirrors that gate to hide the affordance
  // entirely for viewers. Admins short-circuit both checks.
  const myMembershipRole = useAuth((s) =>
    s.user?.memberships.find((m) => m.merchant_id === currentMerchantId)?.role,
  );
  const canVerify =
    isAdmin ||
    myMembershipRole === "owner" ||
    myMembershipRole === "operator";

  const [verifyOpen, setVerifyOpen] = useState(false);

  // Parse the param defensively — NaN-on-malformed is fine because
  // the useBeneficiary hook's enabled clause already gates on
  // beneficiaryId > 0, so a bad URL becomes a "no data yet" render
  // instead of a fetch against /beneficiaries/NaN that the backend
  // would 400 anyway.
  const beneficiaryId =
    bidParam != null && /^\d+$/.test(bidParam) ? Number(bidParam) : null;

  const { data, isLoading, isError, error, refetch, isFetching } =
    useBeneficiary(beneficiaryId);

  // Aggregate stats from the payouts list. These are derived in the
  // render so they stay in lockstep with the table below — no risk of
  // the header showing one set of numbers and the table another.
  const payouts = data?.payouts ?? [];
  const totals = payouts.reduce(
    (acc, p) => {
      acc.total += p.amount_minor;
      if (p.status === "success") acc.success += p.amount_minor;
      if (p.status === "failure") acc.failure += p.amount_minor;
      if (p.status === "needs_approval" || p.status === "pending")
        acc.inFlight += p.amount_minor;
      return acc;
    },
    { total: 0n, success: 0n, failure: 0n, inFlight: 0n },
  );

  // Pre-pick a currency for header anchors. Almost always INR;
  // fallback to "INR" for the empty-state render.
  const currency = payouts[0]?.currency ?? "INR";

  if (currentMerchantId == null) {
    return (
      <div className="page">
        <div className="page-head">
          <h1>Beneficiary</h1>
        </div>
        <div className="card flat">
          <div className="empty">
            <p>
              Pick a merchant from the switcher to see this beneficiary's
              detail. Beneficiaries belong to one merchant and can't be
              viewed cross-tenant.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (beneficiaryId == null) {
    return (
      <div className="page">
        <div className="page-head">
          <h1>Beneficiary</h1>
        </div>
        <div className="card flat">
          <div className="empty">
            <p>Invalid beneficiary id in the URL.</p>
            <button
              className="btn"
              type="button"
              onClick={() => navigate("/beneficiaries")}
              style={{ marginTop: 12 }}
            >
              Back to beneficiaries
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">
            Console ·{" "}
            <Link to="/beneficiaries" style={{ color: "var(--ink-2)" }}>
              Beneficiaries
            </Link>
          </div>
          <h1>
            {data?.beneficiary.label ?? (isLoading ? "Loading…" : "Beneficiary")}
          </h1>
          <div className="sub">
            {isLoading
              ? "Loading payment history…"
              : isError
                ? "Couldn't load beneficiary"
                : `${payouts.length} payout${payouts.length === 1 ? "" : "s"} on file`}
          </div>
        </div>
        <div className="actions" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Aggregate "Sent" total — anchors the page like the
              other money anchors across the panel. Hidden during
              the loading skeleton so the layout doesn't flash a
              "₹0 total" before real data arrives. */}
          {!isLoading && !isError && payouts.length > 0 && (
            <div style={{ textAlign: "right" }}>
              <div
                className="eyebrow"
                style={{ fontSize: 10, letterSpacing: "0.06em" }}
              >
                Total sent (success)
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.15,
                }}
              >
                {fmtCcyMinor(totals.success, currency)}
              </div>
            </div>
          )}
          <button
            className="btn ghost sm"
            type="button"
            onClick={() => void refetch()}
            disabled={isLoading}
            title="Refresh"
          >
            <I.Refresh
              size={12}
              style={{
                animation: isFetching
                  ? "spin 800ms linear infinite"
                  : undefined,
              }}
            />{" "}
            Refresh
          </button>
        </div>
      </div>

      {/* ── Bene info card ───────────────────────────────────────── */}
      {!isError && (
        <div className="card flat">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              padding: 16,
            }}
          >
            <InfoCell
              label="Account"
              value={data?.beneficiary.account_no ?? "—"}
              mono
            />
            <InfoCell label="IFSC" value={data?.beneficiary.ifsc ?? "—"} mono />
            <InfoCell
              label="Email"
              value={data?.beneficiary.email ?? <span className="dim">—</span>}
            />
            <InfoCell
              label="Phone"
              value={data?.beneficiary.phone ?? <span className="dim">—</span>}
              mono
            />
            <InfoCell
              label="Provider registration"
              value={
                data?.beneficiary.is_registered ? (
                  <Pill tone="pos">Registered</Pill>
                ) : (
                  <Pill tone="warn">Unregistered</Pill>
                )
              }
            />
            {/* Penny-drop verification state. A bene with provider
                registration but no verified_at is the typical setup
                behind upstream code 900 — the destination bank never
                confirmed the account is live. Render a Verify CTA
                inline for operator+ when unverified. */}
            <InfoCell
              label="Account verification"
              value={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {data?.beneficiary.verified_at ? (
                    <>
                      <Pill tone="pos">Verified</Pill>
                      <span className="dim" style={{ fontSize: 11 }}>
                        {formatDateTime(data.beneficiary.verified_at)}
                      </span>
                    </>
                  ) : (
                    <>
                      <Pill tone="warn">Not verified</Pill>
                      {canVerify && (
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() => setVerifyOpen(true)}
                          title="Run penny-drop AccountVerification"
                        >
                          Verify now
                        </button>
                      )}
                    </>
                  )}
                </div>
              }
            />
            <InfoCell
              label="Beneficiary id"
              value={`#${data?.beneficiary.id ?? "—"}`}
              mono
            />
          </div>
        </div>
      )}

      {canVerify && data && beneficiaryId != null && currentMerchantId != null && (
        <VerifyBeneficiaryModal
          merchantId={currentMerchantId}
          beneficiaryId={beneficiaryId}
          beneName={data.beneficiary.label}
          open={verifyOpen}
          onClose={() => setVerifyOpen(false)}
        />
      )}

      {/* ── Status distribution chip ─────────────────────────────── */}
      {!isLoading && !isError && payouts.length > 0 && (
        <div
          className="dim"
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            fontSize: 12,
            marginTop: 12,
            padding: "0 4px",
          }}
        >
          <span>Total committed: {fmtCcyMinor(totals.total, currency)}</span>
          {totals.success > 0n && (
            <span style={{ color: "var(--ok, #18794e)" }}>
              · settled {fmtCcyMinor(totals.success, currency)}
            </span>
          )}
          {totals.inFlight > 0n && (
            <span>· in flight {fmtCcyMinor(totals.inFlight, currency)}</span>
          )}
          {totals.failure > 0n && (
            <span style={{ color: "var(--warn, #b42318)" }}>
              · failed {fmtCcyMinor(totals.failure, currency)}
            </span>
          )}
        </div>
      )}

      {/* ── Payouts table ───────────────────────────────────────── */}
      <div className="card flat" style={{ marginTop: 12 }}>
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading payouts…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <p>Couldn't load this beneficiary's payouts.</p>
            <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
              {error instanceof Error ? error.message : "Unknown error"}
            </div>
          </div>
        ) : payouts.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Upload size={16} />
            </div>
            <h3>No payouts yet</h3>
            <p>
              This beneficiary hasn't received any payouts on this merchant.
              Once a payout is initiated, it'll show up here.
            </p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Request ID</th>
                <th className="num">Amount</th>
                <th>UTR / Txn</th>
                <th>Created</th>
                <th>Status</th>
                <th>Batch</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr
                  key={p.id}
                  // Admin-only drill-down — the per-payout page shows
                  // provider payload + audit trail. Merchants stay on
                  // a read-only row with status / utr only.
                  role={isAdmin ? "button" : undefined}
                  tabIndex={isAdmin ? 0 : undefined}
                  onClick={
                    isAdmin
                      ? () => navigate(`/payouts/${p.id}`)
                      : undefined
                  }
                  onKeyDown={
                    isAdmin
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/payouts/${p.id}`);
                          }
                        }
                      : undefined
                  }
                  style={isAdmin ? { cursor: "pointer" } : undefined}
                  title={isAdmin ? `Open pay_${p.id}` : undefined}
                >
                  <td className="mono">{p.request_id}</td>
                  <td className="num">
                    {fmtCcyMinor(p.amount_minor, p.currency)}
                  </td>
                  <td className="mono dim" style={{ fontSize: 11 }}>
                    {p.utr_no ?? p.provider_txn_id ?? "—"}
                  </td>
                  <td className="dim">{formatDateTime(p.created_at)}</td>
                  <td>
                    <StatusPill status={p.status} />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* Link straight to the batch the payout
                        belongs to, when it came from a CSV upload.
                        Single-payout / M2M rows have batch_id=null
                        and show a dash. stopPropagation so clicking
                        the batch link doesn't ALSO trigger the
                        row-level drill-down navigation. */}
                    {p.batch_id != null ? (
                      <Link
                        to={`/payouts/batches/${p.batch_id}`}
                        className="mono"
                      >
                        batch_{p.batch_id}
                      </Link>
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InfoCell({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        className="eyebrow"
        style={{ fontSize: 10, letterSpacing: "0.06em", marginBottom: 4 }}
      >
        {label}
      </div>
      <div className={mono ? "mono" : undefined} style={{ fontSize: 14 }}>
        {value}
      </div>
    </div>
  );
}

// humanizeVerifyFailure — translate the backend's verify_failed envelope
// (provider_code + provider_message captured from the upstream rail) into
// operator-readable copy.
//
// PDF review 26 May 26 round 2 ("hide the name of the provider for the
// merchant"): the user-facing copy never names the provider. Admins who
// need the upstream details can open /admin/provider-logs for the captured
// request/response pair.
//
// Notable rewrites:
//
//   • "Runtime error occured - Warning exception occured" — destination
//     bank's verification check threw a PHP-level warning. Almost always
//     means the account doesn't exist at that bank. The raw text reads
//     like OUR backend crashed and sends operators chasing the wrong
//     hypothesis.
//
//   • code "transaction_pending" — on the verify path this is the rail's
//     mapper falling back to "010" when the real semantic is closer to
//     "could not verify".
function humanizeVerifyFailure(detail: {
  provider_code: string;
  provider_message: string;
}): string {
  const msg = detail.provider_message ?? "";
  const code = detail.provider_code ?? "";

  // "Runtime error / Warning exception" — destination bank rejected the
  // account (most common: account doesn't exist at the destination bank).
  if (msg.toLowerCase().includes("runtime error")) {
    return (
      "The destination bank could not confirm this account. " +
      "Either the account number or IFSC is wrong, or the verification " +
      "rail is failing. Verify the account number and IFSC against the " +
      "bank's records and try again."
    );
  }

  if (code === "account_not_found") {
    return (
      "The destination bank says this account does not exist. " +
      "Double-check the account number and IFSC against the bank's records."
    );
  }

  if (code === "invalid_hash") {
    return (
      "The verification rail rejected our request signature. " +
      "This is a credentials / configuration issue — alert ops."
    );
  }

  if (code === "invalid_credentials") {
    return (
      "The verification rail rejected our credentials. " +
      "Alert ops to review the bank rail configuration."
    );
  }

  if (code === "unauthorized_user" || code === "token_expired") {
    return (
      "Our verification session is invalid or expired. Retry — the next " +
      "call will refresh it. If retry still fails, alert ops."
    );
  }

  if (code === "provider_service_down") {
    return (
      "The verification rail is reporting their service is down. " +
      "Retry in a few minutes."
    );
  }

  // Unknown shape — surface the code/message generically. Admins can dig
  // into the full request/response via /admin/provider-logs.
  const parts: string[] = ["Could not verify this account."];
  if (code) parts.push(`Code: ${code}.`);
  if (msg) parts.push(`Message: "${msg}".`);
  parts.push("Try again, or alert ops if it persists.");
  return parts.join(" ");
}

// ─── VerifyBeneficiaryModal ──────────────────────────────────────────────────
//
// Penny-drop / AccountVerification modal. Picks a bank rail (auto-selects
// when there's exactly one IDFC rail; shows a dropdown otherwise),
// fires POST /v1/merchants/:id/beneficiaries/:bid/verify, and surfaces
// the returned account_name inline so the operator can sanity-check
// against the name on file. On success the beneficiary query
// invalidates so the parent re-renders with verified_at populated.

function VerifyBeneficiaryModal({
  merchantId,
  beneficiaryId,
  beneName,
  open,
  onClose,
}: {
  merchantId: number;
  beneficiaryId: number;
  beneName: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: rails = [] } = useBankAccounts();

  // IDFC-only rails are eligible — Buckzy has no penny-drop equivalent
  // today. Filtering at the panel layer avoids surfacing a rail the
  // backend would reject with provider_not_supported. The response-side
  // provider token is the neutral "idfc" (publicProvider wire rename).
  const eligible = rails.filter(
    (r) => r.provider === "idfc" && r.status === "active",
  );

  const [bankAccountId, setBankAccountId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-seed on open. Auto-select the rail when there's only one.
  useEffect(() => {
    if (open) {
      setBankAccountId(eligible.length === 1 ? String(eligible[0].id) : "");
      setSubmitting(false);
      setAccountName(null);
      setError(null);
    }
    // eligible.length is the only relevant change signal — depending on
    // `eligible` itself would re-run on every render due to filter()
    // returning a new array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eligible.length]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (bankAccountId === "") return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await verifyBeneficiary(
        merchantId,
        beneficiaryId,
        Number(bankAccountId),
      );
      setAccountName(result.account_name);
      void qc.invalidateQueries({
        queryKey: ["beneficiary", merchantId, beneficiaryId],
      });
    } catch (err) {
      // The backend's 422 verify_failed response carries provider_code +
      // provider_message — the operator needs the actual upstream reason
      // to act on the failure. We render specifically per case rather than
      // surface IDFC's raw wording, which is hostile / misleading in
      // the common failures.
      const detail = extractVerifyFailedDetail(err);
      if (detail !== null) {
        setError(humanizeVerifyFailure(detail));
      } else {
        setError(
          apiErrorMessage(
            err,
            "Could not verify the beneficiary. Check the rail credentials and try again.",
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const noRails = eligible.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={submitting}
      title={`Verify ${beneName}`}
      foot={
        accountName ? (
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button
              className="btn ghost"
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              className="btn accent"
              type="submit"
              form="verify-bene-form"
              disabled={submitting || bankAccountId === "" || noRails}
            >
              {submitting ? "Verifying…" : "Run penny-drop"}
            </button>
          </>
        )
      }
    >
      {noRails ? (
        <div className="dim" style={{ fontSize: 13 }}>
          No active bank rail configured on this merchant. Verification runs
          through a configured bank rail — add or activate one first.
        </div>
      ) : accountName ? (
        <div className="stack-16">
          <div
            className="row gap-8"
            style={{ color: "var(--pos)", fontSize: 13 }}
          >
            <I.Check size={14} stroke={2} /> Account verified
          </div>
          <div>
            <div
              className="eyebrow"
              style={{ fontSize: 10, letterSpacing: "0.06em", marginBottom: 4 }}
            >
              Destination bank returned
            </div>
            <div style={{ fontSize: 14 }}>{accountName}</div>
            <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
              Compare this name to the name on file ({beneName}). A mismatch
              means the account number routes to a different person — do NOT
              send payouts until you've confirmed who that is.
            </div>
          </div>
        </div>
      ) : (
        <form id="verify-bene-form" onSubmit={handleSubmit} className="stack-16">
          <div className="dim" style={{ fontSize: 12 }}>
            {/* PDF review 26 May 26 (round 2): keep this copy scoped to
                "we're checking the account is real" without naming the
                upstream provider. Merchants don't need to know which
                provider runs the verify — they just need to know it
                doesn't move money. */}
            Verify the destination bank account. We run a lookup that
            confirms the account is live at the destination bank and
            returns the holder name on file — no money is moved.
          </div>

          <div className="field">
            <label className="label" htmlFor="verify-rail">
              Bank rail
            </label>
            <select
              id="verify-rail"
              className="input"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              disabled={eligible.length === 1}
            >
              {eligible.length !== 1 && (
                <option value="">Select a rail…</option>
              )}
              {eligible.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label} (ba_{r.id})
                </option>
              ))}
            </select>
            {eligible.length === 1 && (
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                Only one active bank rail on this merchant — pre-selected.
              </div>
            )}
          </div>

          {error && (
            <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
              {error}
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
